import styled, { keyframes } from 'styled-components';

var fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

var shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

export var Container = styled.div`
  padding: 16px;
`;

export var BannerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export var Banner = styled.div`
  background: #ffffff;
  border-radius: 4px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #f0f0f0;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  animation: ${fadeIn} 0.3s ease-out;
  animation-fill-mode: both;
  animation-delay: ${function(props) { return (props.index || 0) * 0.05; }}s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    border-color: #d9d9d9;
  }

  &:active {
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

export var BannerLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export var BannerIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: ${function(props) { return props.color || '#f5f5f5'; }};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 22px;
    color: #ffffff;
  }
`;

export var BannerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export var BannerTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: #262626;
  margin: 0;
  line-height: 1.4;
`;

export var BannerSubtitle = styled.span`
  font-size: 13px;
  color: #8c8c8c;
`;

export var BannerRight = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
    gap: 16px;
  }
`;

export var MetricsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

export var Metric = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 60px;
`;

export var MetricValue = styled.span`
  font-size: 20px;
  font-weight: 600;
  color: ${function(props) { return props.color || '#262626'; }};
  line-height: 1;
`;

export var MetricLabel = styled.span`
  font-size: 11px;
  color: #8c8c8c;
  margin-top: 4px;
`;

export var ConversionTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: ${function(props) {
    var rate = props.rate || 0;
    if (rate >= 30) return '#f6ffed';
    if (rate >= 15) return '#fff7e6';
    return '#f5f5f5';
  }};
  color: ${function(props) {
    var rate = props.rate || 0;
    if (rate >= 30) return '#52c41a';
    if (rate >= 15) return '#faad14';
    return '#8c8c8c';
  }};
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

export var ArrowIcon = styled.div`
  color: #d9d9d9;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;

  svg {
    font-size: 14px;
  }

  ${Banner}:hover & {
    color: #8c8c8c;
    transform: translateX(4px);
  }
`;

export var SkeletonBanner = styled.div`
  background: #ffffff;
  border-radius: 4px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  animation: ${fadeIn} 0.3s ease-out;
  animation-fill-mode: both;
  animation-delay: ${function(props) { return (props.index || 0) * 0.1; }}s;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

export var SkeletonLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export var SkeletonIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f5f5f5 25%, #e8e8e8 50%, #f5f5f5 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
`;

export var SkeletonText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export var SkeletonTitle = styled.div`
  height: 18px;
  width: 180px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f5f5f5 25%, #e8e8e8 50%, #f5f5f5 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
`;

export var SkeletonSubtitle = styled.div`
  height: 14px;
  width: 80px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f5f5f5 25%, #e8e8e8 50%, #f5f5f5 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
`;

export var SkeletonRight = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
    gap: 16px;
  }
`;

export var SkeletonMetricsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

export var SkeletonMetric = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

export var SkeletonMetricValue = styled.div`
  width: 40px;
  height: 24px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f5f5f5 25%, #e8e8e8 50%, #f5f5f5 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
`;

export var SkeletonMetricLabel = styled.div`
  width: 50px;
  height: 12px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f5f5f5 25%, #e8e8e8 50%, #f5f5f5 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
`;

export var SkeletonConversionTag = styled.div`
  width: 90px;
  height: 26px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f5f5f5 25%, #e8e8e8 50%, #f5f5f5 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
`;

export var SkeletonArrow = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 2px;
  background: linear-gradient(90deg, #f5f5f5 25%, #e8e8e8 50%, #f5f5f5 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
`;

export var EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
  background: #ffffff;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

export var EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;

  svg {
    font-size: 28px;
    color: #bfbfbf;
  }
`;

export var EmptyTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: #262626;
  margin: 0 0 8px 0;
`;

export var EmptyDescription = styled.p`
  font-size: 14px;
  color: #8c8c8c;
  margin: 0;
  max-width: 300px;
`;
